import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Plus, Edit2, Trash2, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RatesAndServices = ({ token }) => {
  const [shippingRates, setShippingRates] = useState([]);
  const [additionalServices, setAdditionalServices] = useState([]);
  const [showRateForm, setShowRateForm] = useState(false);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [editingService, setEditingService] = useState(null);

  const [rateForm, setRateForm] = useState({
    shipping_type: 'Maritimo',
    customer_type: 'publico',
    rate_per_unit: 0,
    minimum_chargeable: 0,
    unit: 'lb',
    repack_cost_min: 5.0,
    repack_cost_max: 10.0
  });

  const [serviceForm, setServiceForm] = useState({
    service_name: '',
    description: '',
    price: 0,
    is_percentage: false
  });

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ratesRes, servicesRes] = await Promise.all([
        axios.get(`${API}/admin/shipping-rates`, axiosConfig),
        axios.get(`${API}/admin/additional-services`, axiosConfig)
      ]);
      setShippingRates(ratesRes.data);
      setAdditionalServices(servicesRes.data);
    } catch (error) {
    }
  };

  const handleCreateRate = async () => {
    try {
      await axios.post(`${API}/admin/shipping-rates`, rateForm, axiosConfig);
      alert('✅ Tarifa creada exitosamente');
      setShowRateForm(false);
      setRateForm({
        shipping_type: 'Maritimo',
        customer_type: 'publico',
        rate_per_unit: 0,
        minimum_chargeable: 0,
        unit: 'lb',
        repack_cost_min: 5.0,
        repack_cost_max: 10.0
      });
      fetchData();
    } catch (error) {
      alert('Error al crear tarifa: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateRate = async (rateId) => {
    try {
      await axios.put(`${API}/admin/shipping-rates/${rateId}`, editingRate, axiosConfig);
      alert('✅ Tarifa actualizada');
      setEditingRate(null);
      fetchData();
    } catch (error) {
      alert('Error al actualizar: ' + error.message);
    }
  };

  const handleDeleteRate = async (rateId) => {
    if (!window.confirm('¿Eliminar esta tarifa?')) return;
    try {
      await axios.delete(`${API}/admin/shipping-rates/${rateId}`, axiosConfig);
      alert('✅ Tarifa eliminada');
      fetchData();
    } catch (error) {
      alert('Error al eliminar: ' + error.message);
    }
  };

  const handleCreateService = async () => {
    try {
      await axios.post(`${API}/admin/additional-services`, serviceForm, axiosConfig);
      alert('✅ Servicio creado exitosamente');
      setShowServiceForm(false);
      setServiceForm({
        service_name: '',
        description: '',
        price: 0,
        is_percentage: false
      });
      fetchData();
    } catch (error) {
      alert('Error al crear servicio: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateService = async (serviceId) => {
    try {
      await axios.put(`${API}/admin/additional-services/${serviceId}`, editingService, axiosConfig);
      alert('✅ Servicio actualizado');
      setEditingService(null);
      fetchData();
    } catch (error) {
      alert('Error al actualizar: ' + error.message);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('¿Eliminar este servicio?')) return;
    try {
      await axios.delete(`${API}/admin/additional-services/${serviceId}`, axiosConfig);
      alert('✅ Servicio eliminado');
      fetchData();
    } catch (error) {
      alert('Error al eliminar: ' + error.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Shipping Rates Section */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Tarifas de Envío</CardTitle>
              <CardDescription>Gestiona las tarifas de envío marítimo y aéreo</CardDescription>
            </div>
            <Button onClick={() => setShowRateForm(!showRateForm)}>
              <Plus className="mr-2 h-4 w-4" />
              Nueva Tarifa
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showRateForm && (
            <div className="mb-6 p-4 border rounded-lg bg-gray-50">
              <h3 className="font-semibold mb-4">Crear Nueva Tarifa</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de Envío</Label>
                  <Select value={rateForm.shipping_type} onValueChange={(v) => setRateForm({...rateForm, shipping_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Maritimo">Marítimo</SelectItem>
                      <SelectItem value="Aereo">Aéreo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo de Cliente</Label>
                  <Select value={rateForm.customer_type} onValueChange={(v) => setRateForm({...rateForm, customer_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="publico">Público</SelectItem>
                      <SelectItem value="alumno">Alumno</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Unidad</Label>
                  <Select value={rateForm.unit} onValueChange={(v) => setRateForm({...rateForm, unit: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lb">Libras (lb)</SelectItem>
                      <SelectItem value="ft3">Pies Cúbicos (ft³)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tarifa por Unidad ($)</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={rateForm.rate_per_unit}
                    onChange={(e) => setRateForm({...rateForm, rate_per_unit: parseFloat(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Mínimo Cobrable</Label>
                  <Input 
                    type="number" 
                    step="0.1"
                    value={rateForm.minimum_chargeable}
                    onChange={(e) => setRateForm({...rateForm, minimum_chargeable: parseFloat(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Costo Reempaque Mín ($)</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={rateForm.repack_cost_min}
                    onChange={(e) => setRateForm({...rateForm, repack_cost_min: parseFloat(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Costo Reempaque Máx ($)</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={rateForm.repack_cost_max}
                    onChange={(e) => setRateForm({...rateForm, repack_cost_max: parseFloat(e.target.value)})}
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleCreateRate}>Crear Tarifa</Button>
                <Button variant="outline" onClick={() => setShowRateForm(false)}>Cancelar</Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {shippingRates.map(rate => (
              <div key={rate.id} className="border p-4 rounded-lg flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                      {rate.shipping_type}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                      {rate.customer_type}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${rate.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {rate.is_active ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                  <p className="text-sm"><strong>Tarifa:</strong> ${rate.rate_per_unit} por {rate.unit}</p>
                  <p className="text-sm"><strong>Mínimo:</strong> {rate.minimum_chargeable} {rate.unit}</p>
                  <p className="text-sm"><strong>Reempaque:</strong> ${rate.repack_cost_min} - ${rate.repack_cost_max}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleDeleteRate(rate.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {shippingRates.length === 0 && (
              <p className="text-center text-gray-500 py-8">No hay tarifas configuradas</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Additional Services Section */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Servicios Adicionales</CardTitle>
              <CardDescription>Gestiona servicios extras que puedes ofrecer</CardDescription>
            </div>
            <Button onClick={() => setShowServiceForm(!showServiceForm)}>
              <Plus className="mr-2 h-4 w-4" />
              Nuevo Servicio
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showServiceForm && (
            <div className="mb-6 p-4 border rounded-lg bg-gray-50">
              <h3 className="font-semibold mb-4">Crear Nuevo Servicio</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre del Servicio</Label>
                  <Input 
                    value={serviceForm.service_name}
                    onChange={(e) => setServiceForm({...serviceForm, service_name: e.target.value})}
                    placeholder="Ej: Seguro de paquete"
                  />
                </div>
                <div>
                  <Label>Precio ($)</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={serviceForm.price}
                    onChange={(e) => setServiceForm({...serviceForm, price: parseFloat(e.target.value)})}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Descripción</Label>
                  <Textarea 
                    value={serviceForm.description}
                    onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
                    placeholder="Descripción del servicio"
                  />
                </div>
                <div className="col-span-2">
                  <label className="flex items-center gap-2">
                    <input 
                      type="checkbox"
                      checked={serviceForm.is_percentage}
                      onChange={(e) => setServiceForm({...serviceForm, is_percentage: e.target.checked})}
                    />
                    <span className="text-sm">Es porcentaje del total</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleCreateService}>Crear Servicio</Button>
                <Button variant="outline" onClick={() => setShowServiceForm(false)}>Cancelar</Button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {additionalServices.map(service => (
              <div key={service.id} className="border p-4 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{service.service_name}</h3>
                    <p className="text-sm text-gray-600">{service.description}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleDeleteService(service.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="mt-3 flex justify-between items-center">
                  <p className="text-xl font-bold text-green-600">
                    ${service.price}{service.is_percentage && '%'}
                  </p>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${service.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {service.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>
            ))}
            {additionalServices.length === 0 && (
              <p className="col-span-2 text-center text-gray-500 py-8">No hay servicios adicionales configurados</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RatesAndServices;
